import React from 'react';
import { AlertTriangle } from 'lucide-react';
import type {
  CreditNoteStockReturnRecord,
  ReturnSources,
  StockReturnEntry,
  StockReturnShortfall,
} from '../../lib/creditNoteApi';
import { formatDate } from '../../utils/dateFormat';
import { sourceKeyOf } from './stockReturnDraft';

/**
 * What an ISSUED or CANCELLED Item Return did to United stock (D44 #8, #35, #39,
 * VR-4), read-only: per line, what went back and into which batch, and on a
 * cancelled note what came back out. Every figure is what the server stored.
 */

export type SummaryRow = {
  lineNumber: number | null;
  description: string;
  entries: StockReturnEntry[];
};

function withUnit(value: number, unit: string): string {
  return `${value} ${unit || ''}`.trim();
}

function boxesText(seqs: number[]): string {
  return `${seqs.length === 1 ? 'Box' : 'Boxes'} ${seqs.join(', ')}`;
}

function lineLabel(lineNumber: number | null, name: string): string {
  if (lineNumber === null) return name || 'A line';
  return `Line ${lineNumber}${name ? ` (${name})` : ''}`;
}

/**
 * On a cancelled note: goods that had already left their batch and could not be
 * taken back out (#39). Shown prominently: the count is worth checking.
 */
export const StockReturnShortfallNotice: React.FC<{ shortfall: StockReturnShortfall[] }> = ({ shortfall }) => {
  if (!shortfall.length) return null;
  return (
    <div
      data-testid="stock-return-shortfall"
      className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-3 text-xs text-amber-900"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle aria-hidden="true" size={18} className="mt-0.5 shrink-0 text-amber-600" />
        <div className="min-w-0 space-y-1">
          <p className="font-semibold">Not all the goods could be taken back out of stock</p>
          {shortfall.map((s, i) => {
            const what = s.caseSeqs.length
              ? `${boxesText(s.caseSeqs)} (${withUnit(s.qtyLevel1, s.unit)})`
              : withUnit(s.qtyLevel1, s.unit);
            return (
              <p key={`${s.lineNumber}-${s.batchLabel}-${i}`}>
                {lineLabel(s.lineNumber, s.itemName)}: {what} had already left {s.batchLabel} when this note was
                cancelled and could not be taken back out. Check the count.
              </p>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export const StockReturnSummary: React.FC<{
  status: string;
  rows: SummaryRow[];
  record: CreditNoteStockReturnRecord | null;
  /** For batch and shipment names; quantities alone are shown without it. */
  sources: ReturnSources | null;
}> = ({ status, rows, record, sources }) => {
  const byKey = new Map((sources?.lines || []).flatMap((line) => line.sources.map((s) => [s.key, s] as const)));
  const returned = rows
    .map((row) => ({ ...row, entries: row.entries.filter((e) => e.returnMovementId) }))
    .filter((row) => row.entries.length > 0);

  if (!returned.length) {
    return (
      <p className="text-xs text-slate-500" data-testid="stock-return-summary">
        {status === 'cancelled'
          ? 'Nothing went back into United stock: the note was cancelled before it was issued.'
          : 'No goods went back into United stock on this note.'}
      </p>
    );
  }

  return (
    <div className="space-y-2 text-xs text-slate-700" data-testid="stock-return-summary">
      {record?.returnedAt && <p>Went back into United stock on {formatDate(record.returnedAt, '')}.</p>}
      {status === 'cancelled' && record?.takenBackAt && (
        <p>
          The note was cancelled on {formatDate(record.takenBackAt, '')}: the goods came back out of stock as far as they
          were still free.
        </p>
      )}
      <ul className="space-y-2">
        {returned.map((row, rowIndex) => (
          <li key={`${row.lineNumber}-${rowIndex}`} className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
            <div className="font-medium text-slate-800">{lineLabel(row.lineNumber, row.description)}</div>
            <ul className="mt-1 space-y-1">
              {row.entries.map((e, i) => {
                const source = byKey.get(sourceKeyOf(e));
                const unit = source?.unitLevel1 || '';
                const what = e.caseSeqs.length
                  ? `${boxesText(e.caseSeqs)} (${withUnit(e.qtyLevel1, unit)})`
                  : withUnit(e.qtyLevel1, unit);
                const intoNewBatch = Boolean(e.returnLayerId && e.returnLayerId !== e.layerId);
                const into = intoNewBatch
                  ? source?.location === 'pantry'
                    ? "a new United batch at the Pantry batch's cost and expiry"
                    : 'a new United batch'
                  : source?.batchLabel || 'the batch it was shipped from';
                const takenBack =
                  status === 'cancelled' && typeof e.takenBackLevel1 === 'number'
                    ? e.takenBackLevel1 > 0
                      ? `${
                          e.takenBackCaseSeqs && e.takenBackCaseSeqs.length
                            ? `${boxesText(e.takenBackCaseSeqs)} (${withUnit(e.takenBackLevel1, unit)})`
                            : withUnit(e.takenBackLevel1, unit)
                        } taken back out.`
                      : 'Nothing could be taken back out.'
                    : '';
                return (
                  <li key={`${sourceKeyOf(e)}-${i}`}>
                    <span className="tabular-nums">{what}</span> back into {into}
                    {source ? `, from ${source.shipmentLabel}` : ''}.{takenBack ? ` ${takenBack}` : ''}
                  </li>
                );
              })}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default StockReturnSummary;
